/**
 * Compass Impact Panel — structural counterpart to the Layered RCA.
 * On the analyst's click (each run is a paid, 20–90s AI call), sends the
 * ticket text to the external Compass service, which reasons over the LRP
 * code + schema graph (Java call graphs, procedures, tables/fields, rules)
 * through read-only lookups and returns a grounded markdown explanation
 * with its evidence trail. Failure here never affects the rest of the page.
 * See docs/flow/COMPASS.md for the service contract.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Compass, Copy, Loader2, MessageSquare, RefreshCw, ShieldAlert } from 'lucide-react';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import { useTicketChatStore } from '../../store/ticketChatStore';
import { CompassEvidenceAccordion } from '../common/CompassEvidence';
import {
  CompassAnalysis,
  CompassDataFix,
  dataFixScripts,
  CompassHint,
  getSavedAnalysis,
  pollJob,
  saveAnalysis,
  submitAnalysis,
} from '../../services/compassApi';

/** Serif stack per the Compass styling spec — the story reads like prose,
 *  not UI chrome. */
const STORY_FONT =
  "Charter, 'Bitstream Charter', 'Sitka Text', Cambria, Georgia, serif";

/** Split the Compass explanation into the plain-language story (part A)
 *  and the technical sections (part B). New template (2026-08-30) splits
 *  at the literal "**The cause**" (mackey's convention); older saved
 *  analyses used "**What's involved**". Missing marker (legacy, degraded
 *  responses) → no split, render everything as one block — never break
 *  on legacy. */
/** Strip the "Unverified names" footer from the explanation (removed from
 *  display 2026-08-31, Anthony's call — the checker still runs on Compass;
 *  this also covers saved analyses generated before Compass stops emitting
 *  it). The footer runs from its heading to the next section or the end. */
function stripUnverifiedFooter(md: string): string {
  return md.replace(/\n[^\n]*Unverified names[\s\S]*?(?=\n#{1,3} |\n\*\*[A-Z]|$)/, '\n');
}

function splitExplanation(md: string): { story: string | null; technical: string } {
  const marker = md.match(/\*\*The cause\*\*|\*\*What[’']s involved\*\*/);
  if (!marker || marker.index === undefined || marker.index <= 0) {
    return { story: null, technical: md };
  }
  const story = md
    .slice(0, marker.index)
    .replace(/\*\*The story[^*]*\*\*:?\s*/i, '')
    .trim();
  if (!story) return { story: null, technical: md };
  return { story, technical: md.slice(marker.index).trim() };
}

interface Props {
  /** Ticket text to analyze (condensed summary preferred). */
  issueText: string;
  /** Ticket key + context for opening the ticket chat seeded with THIS
   *  panel's analysis — the code/schema view is what the conversation
   *  interrogates. */
  ticketKey?: string;
  modules?: string[];
  entities?: unknown[];
  /** Cache key — one Compass run per analysis, survives re-renders. */
  analysisId?: number | null;
  /** Serialized SQL evidence (buildDataContext) — raw observations only.
   *  Lets Compass confirm/rule out instead of hypothesize. Optional and
   *  fully backward compatible. */
  dataContext?: string;
  /** Candidate graph keys (buildHints) — soft leads the analyst verifies,
   *  ranked most-likely first. Also used in degraded mode. */
  hints?: CompassHint[];
}

export function CompassImpactPanel({ issueText, ticketKey, modules, entities, analysisId, dataContext, hints }: Props) {
  const [result, setResult] = useState<CompassAnalysis | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  /** Seconds elapsed while polling — the wait is minutes, so silence reads
   *  as a hang. */
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [techOpen, setTechOpen] = useState(false);
  const [jsonCopied, setJsonCopied] = useState(false);
  // Per-analysis in-memory cache so re-renders never re-bill within a visit;
  // durable storage lives on the analysis row (migration 033).
  const cache = useRef<Map<string, CompassAnalysis>>(new Map());
  const cacheKey = String(analysisId ?? issueText.slice(0, 80));

  // Load the persisted result (instant, free) when the analysis opens.
  useEffect(() => {
    if (!analysisId) return;
    let cancelled = false;
    getSavedAnalysis(analysisId)
      .then((stored) => {
        if (cancelled || !stored.response) return;
        cache.current.set(cacheKey, stored.response);
        setResult(stored.response);
        setSavedAt(stored.saved_at);
      })
      .catch(() => {
        /* best-effort restore — the Run button still works */
      });
    return () => {
      cancelled = true;
    };
  }, [analysisId, cacheKey]);

  const run = async (force = false) => {
    if (!force) {
      const cached = cache.current.get(cacheKey);
      if (cached) {
        setResult(cached);
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      // Submit-and-poll, not a held-open request. A direct call aborted at
      // 120s while the backend kept working: Compass measured 104s and 123s
      // for real analyses, so the second finished with nobody listening and
      // the paid work was thrown away as "Compass unavailable". CR Analysis
      // moved to this path in 1.69.10; this panel was left behind.
      const jobKey = analysisId ? String(analysisId) : `t:${cacheKey}`;
      await submitAnalysis('support', jobKey, issueText, { hints, dataContext });
      const job = await pollJob('support', jobKey, { onTick: setElapsed });
      if (job.status !== 'done' || !job.response) {
        throw new Error(job.error || 'Compass analysis did not complete');
      }
      const r = job.response;
      cache.current.set(cacheKey, r);
      setResult(r);
      setSavedAt(null);
      // Persist for every future viewer of this analysis — best-effort,
      // never blocks showing the result. Degraded responses are not saved
      // (a later run with the AI back up should replace them).
      if (analysisId && !r.degraded) {
        saveAnalysis(analysisId, r)
          .then(() => setSavedAt(new Date().toISOString()))
          .catch(() => {
            /* result still shown; next run retries the save */
          });
      }
    } catch (e) {
      // Attribute the failure to what ACTUALLY failed. The old code showed
      // "Compass unavailable" for every error including our own 120s abort,
      // which named a healthy service as the culprit and sent whoever
      // debugged it looking in the wrong place first (Compass, mesh #27).
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes('CLIENT_GAVE_UP')) {
        setError(
          'Still running — this analysis is taking longer than usual. It has not failed; ' +
            'wait a moment and press Re-run to pick up the result.',
        );
      } else if (msg.includes('429')) {
        setError('Rate limit — try again in a minute.');
      } else if (/\b(502|503|504)\b/.test(msg)) {
        // Gateway codes are the only ones that genuinely say the service
        // behind us could not be reached.
        setError('The analysis service could not be reached. Try again shortly.');
      } else {
        setError(`Analysis could not be completed: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!issueText.trim()) return null;

  return (
    <div className="border border-purple-200 rounded-lg overflow-hidden bg-purple-50">
      <div className="px-4 py-3 bg-purple-100 flex items-center gap-2">
        <Compass size={16} className="text-purple-700" />
        <span className="text-sm font-semibold text-purple-900 flex items-center gap-1.5">
          <span
            className="text-[9px] px-1 py-0.5 rounded-full bg-purple-200 text-purple-800 font-mono"
            title="Code and Database Structure"
          >
            CDS
          </span>
          Code &amp; Database Structure (Compass)
        </span>
        {result && (
          <span className="ml-auto flex items-center gap-2 text-[10px] text-purple-500">
            {ticketKey && (
              <button
                onClick={() => {
                  useTicketChatStore.getState().open({
                    ticketKey,
                    modules: modules ?? [],
                    entities: entities ?? [],
                    analysisMarkdown: result.explanation || '',
                    analysisId: analysisId ?? null,
                  });
                  window.location.hash = '#/ticket-chat';
                }}
                className="flex items-center gap-1 px-2.5 py-1 rounded bg-purple-600 text-white text-[11px] font-medium hover:bg-purple-500 transition-colors"
                title="Interrogate this analysis — answers read the real code (Java, procedures, triggers, XHTML) and query the data"
              >
                <MessageSquare size={11} />
                Chat about this ticket
              </button>
            )}
            {savedAt && (
              <span title={`Stored ${savedAt} — shown to every viewer without re-running`}>
                saved
              </span>
            )}
            {result.model && !result.degraded && (
              <span>
                {result.model} · {(result.elapsed_ms / 1000).toFixed(0)}s
              </span>
            )}
            <button
              onClick={() => {
                navigator.clipboard.writeText(JSON.stringify(result, null, 2));
                setJsonCopied(true);
                setTimeout(() => setJsonCopied(false), 1500);
              }}
              className="flex items-center gap-0.5 text-purple-600 hover:text-purple-800"
              title="Copy the full structured /support/analyze response (explanation, evidence, model, usd, data_fix, graph_snapshot, elapsed_ms) as JSON"
            >
              {jsonCopied ? <Check size={10} className="text-green-600" /> : <Copy size={10} />}
              {jsonCopied ? 'Copied' : 'Copy JSON'}
            </button>
            <button
              onClick={() => run(true)}
              disabled={loading}
              className="flex items-center gap-0.5 text-purple-600 hover:text-purple-800"
              title="Run a fresh Compass analysis (paid AI call, typically 1–3 minutes) — replaces the stored result"
            >
              <RefreshCw size={10} className={loading ? 'animate-spin' : ''} /> Re-run
            </button>
          </span>
        )}
      </div>
      <div className="px-4 py-3 space-y-2">
        {!result && !loading && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => run()}
              className="px-3 py-1.5 text-xs font-medium rounded bg-purple-600 text-white hover:bg-purple-700"
            >
              Check code &amp; database structure
            </button>
            <span className="text-[10px] text-purple-600">
              Impact analysis over the LRP code + schema graph (typically 1–3 minutes).
            </span>
          </div>
        )}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-purple-700">
            <Loader2 size={13} className="animate-spin" />
            {/* A live counter, because the wait runs to minutes and a static
                message makes a working analysis look like a hung one. */}
            Analyzing against the code &amp; database graph… {elapsed}s
            <span className="text-purple-400">(typically 1–3 minutes)</span>
          </div>
        )}
        {error && !loading && (
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-500 italic">{error}</p>
            <button onClick={() => run()} className="text-[10px] text-purple-600 underline">
              Retry
            </button>
          </div>
        )}
        {result && (
          <>
            {result.degraded && (
              <div className="text-[10px] text-purple-700 bg-purple-100 border border-purple-200 rounded p-1.5">
                AI analysis unavailable — showing knowledge-graph facts.
              </div>
            )}
            {/* Two audiences, one response: the story card is what a support
                agent reads to the user; the technical accordion is what they
                forward to a developer. Degraded/legacy content has no story
                marker and falls back to a single block. */}
            {(() => {
              const explanation = stripUnverifiedFooter(result.explanation);
              const { story, technical } = result.degraded
                ? { story: null, technical: explanation }
                : splitExplanation(explanation);
              return (
                <>
                  {story && (
                    <div
                      className="rounded-lg mb-3"
                      style={{
                        background: '#f5f5ff',
                        borderLeft: '3px solid #6366f1',
                        padding: '16px 20px',
                      }}
                    >
                      <div className="text-[13px] font-semibold text-gray-500 mb-1.5">
                        📖 What happened, in plain language
                      </div>
                      <div
                        style={{
                          fontFamily: STORY_FONT,
                          fontSize: '17px',
                          lineHeight: 1.75,
                          maxWidth: '68ch',
                          color: '#1f2937',
                        }}
                      >
                        <MarkdownRenderer content={story} showTableOfContents={false} />
                      </div>
                    </div>
                  )}
                  {story ? (
                    <div>
                      <button
                        onClick={() => setTechOpen((v) => !v)}
                        className="flex items-center gap-1 text-xs font-medium text-purple-700"
                      >
                        {techOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                        Technical details (for developers)
                      </button>
                      {techOpen && (
                        <div
                          className="mt-2"
                          style={{
                            fontFamily: STORY_FONT,
                            fontSize: '16px',
                            lineHeight: 1.7,
                            color: '#1f2937',
                          }}
                        >
                          <MarkdownRenderer content={technical} showTableOfContents={false} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-800 leading-relaxed">
                      <MarkdownRenderer content={technical} showTableOfContents={false} />
                    </div>
                  )}
                </>
              );
            })()}
            {/* Data-fix rendering removed 2026-08-31 (Anthony): the ticket
                chat is now the surface for requesting a data script, so the
                panel no longer carries the declined/proposed block. The
                structured data_fix field still arrives and is ignored. */}
            <CompassEvidenceAccordion evidence={result.evidence} />
          </>
        )}
      </div>
    </div>
  );
}

/** Proposed data-correction script from Compass. REVIEW-ONLY by contract:
 *  Compass never executes anything and this UI must never offer to — copy
 *  for DBA review is the only affordance. */
function DataFixSection({ dataFix }: { dataFix: CompassDataFix | null | undefined }) {
  if (!dataFix) return null;

  const fixScripts = dataFixScripts(dataFix);
  if (!dataFix.fixable_with_data_script || !fixScripts) {
    return (
      <div className="mt-3 text-xs text-gray-600">
        No data-fix script proposed{dataFix.reason ? ` — ${dataFix.reason}` : ''}
      </div>
    );
  }

  const scripts: [string, string][] = [
    ['Preview', fixScripts.preview_sql],
    ['Backup', fixScripts.backup_sql],
    ['Fix', fixScripts.fix_sql],
    ['Rollback', fixScripts.rollback_sql],
  ].filter((s): s is [string, string] => !!s[1]);
  const tables = (fixScripts.tables?.length
    ? fixScripts.tables
    : (dataFix.fixes ?? []).map((f) => f?.table)
  )
    .map((t) => t?.trim())
    .filter((t): t is string => !!t);

  return (
    <div className="mt-3 border border-amber-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-amber-50 flex items-center gap-2">
        <ShieldAlert size={14} className="text-amber-600" />
        <span className="text-xs font-semibold text-gray-800">
          Proposed Data Fix — for DBA review only
        </span>
      </div>
      <div className="px-3 py-2.5 space-y-2.5">
        {dataFix.reason && (
          <p className="text-xs text-gray-600">{dataFix.reason}</p>
        )}
        {tables.length > 0 && (
          <p className="text-xs text-gray-600">
            Tables touched ({tables.length}): {tables.join(', ')} — one shared
            transaction.
          </p>
        )}
        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">
          Nothing is executed by LISA or Compass. Hand these scripts to your DBA:
          4-eyes review, then Hotfix, then production with client approval.
        </p>
        {scripts.map(([name, sql]) => (
          <ScriptBlock key={name} name={name} sql={sql} />
        ))}
      </div>
    </div>
  );
}

function ScriptBlock({ name, sql }: { name: string; sql: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="border border-gray-200 rounded-md overflow-hidden">
      <div className="px-2.5 py-1.5 bg-gray-50 flex items-center justify-between">
        <span className="text-[11px] font-semibold text-gray-600">{name}</span>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(sql);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
          className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800"
        >
          {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="px-2.5 py-2 text-[11px] font-mono text-gray-800 whitespace-pre-wrap overflow-x-auto bg-white">{sql}</pre>
    </div>
  );
}
